import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { TorchSvg } from '@/components/torch/TorchSvg';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import type { AcceptLink } from '@/lib/torchTypes';
import { Flame, Zap, Clock, Users, ArrowRight, Plus } from 'lucide-react';

const GENESIS_A = 'a1b2c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2';
const GENESIS_B = 'f3e2d1c0b9a8f7e6d5c4b3a2f1e0d9c8b7a6f5e4d3c2b1a0f9e8d7c6b5a4f3e2';
const GENESIS_C = '1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6d7e8f9a0b1c2d3e4f5a6b7c8d9e0f1a2b3';

function makeChain(length: number, seed = 0): AcceptLink[] {
  return Array.from({ length }, (_, i) => ({
    holderPubkey: Array.from({ length: 64 }, (_, j) => {
      const byte = (((i + seed) * 7 + j * 13) % 256).toString(16).padStart(2, '0');
      return byte;
    }).join(''),
    zapAmountSats: 21 + i * 10,
    positionIndex: i + 1,
  }));
}

const STEPS = [
  {
    number: '01',
    icon: <Flame className="w-6 h-6 text-orange-400" />,
    title: 'Light a Torch',
    body: 'Mint a torch with a unique generative flame. Give it a name, set how long each holder has before the clock runs out.',
  },
  {
    number: '02',
    icon: <Zap className="w-6 h-6 text-orange-400" />,
    title: 'Pass via Zap',
    body: 'Pick someone on Nostr and zap them directly via Lightning. The zap IS the pass — when the sats land, the torch is theirs. Each pass must be larger than the last.',
  },
  {
    number: '03',
    icon: <Users className="w-6 h-6 text-orange-400" />,
    title: 'Hold & Move',
    body: "The new holder gets a Nostr notification with a link. They log in, see they're holding the flame, and decide who to pass it to next.",
  },
  {
    number: '04',
    icon: <Clock className="w-6 h-6 text-orange-400" />,
    title: 'Survive or Die',
    body: "Every holder has a time window to pass. Miss it and the torch is extinguished immediately — the chain sealed forever. Pass it or kill it.",
  },
];

const FLAME_STAGES = [
  { label: 'Genesis', holders: 1, genesisId: GENESIS_A, seed: 0 },
  { label: '5 holders', holders: 5, genesisId: GENESIS_A, seed: 0 },
  { label: '20 holders', holders: 20, genesisId: GENESIS_A, seed: 0 },
  { label: '60 holders', holders: 60, genesisId: GENESIS_A, seed: 0 },
];

const FLAME_GALLERY = [
  { genesisId: GENESIS_A, holders: 15, seed: 0 },
  { genesisId: GENESIS_B, holders: 15, seed: 1 },
  { genesisId: GENESIS_C, holders: 15, seed: 2 },
];

export function TorchPreview() {
  const stageChains = useMemo(() =>
    FLAME_STAGES.map(s => makeChain(s.holders, s.seed)),
    []
  );

  const galleryChains = useMemo(() =>
    FLAME_GALLERY.map(g => makeChain(g.holders, g.seed)),
    []
  );

  return (
    <Layout>
      {/* Hero */}
      <div className="relative isolate overflow-hidden border-b border-border/40">
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-orange-950/20 via-background to-background" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[250px] -z-10 bg-orange-500/8 rounded-full blur-3xl" />
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-orange-500/15 border border-orange-500/20 mb-6 shadow-[0_0_20px_rgba(249,115,22,0.25)]">
            <Flame className="w-7 h-7 text-orange-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">How Torchlite Works</h1>
          <p className="text-lg text-muted-foreground max-w-xl mx-auto">
            A chain of growing Lightning zaps, passed between Nostr users. Keep the flame alive or watch it die forever.
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {STEPS.map((step) => (
            <Card key={step.number} className="bg-card/60 border-border/50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="shrink-0">
                    <div className="w-10 h-10 rounded-full bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                      {step.icon}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-orange-400 font-semibold">{step.number}</span>
                      <h3 className="font-bold text-lg">{step.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.body}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Flame evolution */}
      <div className="bg-card/30 border-y border-border/40 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold mb-2">The flame grows with every pass</h2>
            <p className="text-muted-foreground">
              Each holder adds a colored ember. The longer the chain, the wilder the fire.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {FLAME_STAGES.map((stage, i) => (
              <div key={stage.label} className="text-center">
                <TorchSvg
                  genesisId={stage.genesisId}
                  chain={stageChains[i]}
                  state="lit"
                  className="w-full h-44 mx-auto"
                />
                <p className="text-xs font-medium text-muted-foreground mt-2">{stage.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* States explainer */}
      <div className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold mb-2">Two states. No mercy.</h2>
          <p className="text-muted-foreground">Pass it in time or kill it. There is no in-between.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {[
            {
              state: 'lit' as const,
              label: '🔥 Lit',
              color: 'border-orange-300 dark:border-orange-700',
              bg: 'bg-orange-50 dark:bg-orange-950/20',
              text: 'text-orange-800 dark:text-orange-200',
              desc: "The torch is in someone's hands. The clock is ticking — zap the next holder before time runs out or the flame dies with you.",
            },
            {
              state: 'extinguished' as const,
              label: '🪦 Extinguished',
              color: 'border-gray-300 dark:border-gray-700',
              bg: 'bg-gray-50 dark:bg-gray-900/20',
              text: 'text-gray-600 dark:text-gray-400',
              desc: 'The holder missed their window. The flame is out — permanently sealed, the full chain of holders preserved forever on Nostr.',
            },
          ].map((s) => (
            <Card key={s.state} className={`border-2 ${s.color} ${s.bg}`}>
              <CardContent className="p-6 text-center">
                <TorchSvg
                  genesisId={GENESIS_B}
                  chain={makeChain(12, 3)}
                  state={s.state}
                  className="w-28 h-36 mx-auto mb-4"
                />
                <p className={`font-bold text-base mb-2 ${s.text}`}>{s.label}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Every torch is unique */}
      <div className="bg-card/30 border-y border-border/40 py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold mb-2">Every torch is unique</h2>
            <p className="text-muted-foreground">
              The flame is generated from the genesis event ID — no two torches ever look the same.
            </p>
          </div>
          <div className="flex items-end justify-center gap-8 md:gap-16">
            {FLAME_GALLERY.map((g, i) => (
              <div key={g.genesisId} className="text-center">
                <TorchSvg
                  genesisId={g.genesisId}
                  chain={galleryChains[i]}
                  state="lit"
                  className="w-24 h-32 md:w-36 md:h-48 mx-auto"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* The mechanic */}
      <div className="max-w-3xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl font-bold mb-2">The zap mechanic</h2>
        </div>
        <div className="space-y-4">
          {[
            { icon: '⚡', title: 'Each pass is a real Lightning payment', body: 'When you pass a torch, you zap the recipient directly via their Lightning address. No custodians, no intermediaries.' },
            { icon: '📈', title: 'Zaps must grow', body: 'Every pass must be larger than the previous one by at least 1 sat. The longer the chain, the higher the minimum — real skin in the game.' },
            { icon: '🔔', title: 'Recipients are notified', body: "When you pass a torch to someone, they get a Nostr notification. One tap and they're on the torch page, ready to pass it on." },
            { icon: '🔒', title: 'Chain is cryptographically verified', body: 'Every pass is a signed Nostr event with a zap receipt as proof. The lineage is public, permanent, and tamper-proof.' },
          ].map((item) => (
            <div key={item.title} className="flex gap-4 p-4 rounded-xl bg-card/50 border border-border/40">
              <span className="text-2xl shrink-0">{item.icon}</span>
              <div>
                <p className="font-semibold mb-1">{item.title}</p>
                <p className="text-sm text-muted-foreground">{item.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="relative isolate bg-gradient-to-b from-background to-orange-950/15 py-16 border-t border-border/40">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_center,rgba(249,115,22,0.06)_0%,transparent_70%)]" />
        <div className="max-w-xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-bold mb-3">Ready?</h2>
          <p className="text-muted-foreground mb-8">
            Light a torch, pick someone on Nostr, and zap them. That's it.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/create">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 gap-2">
                <Plus className="w-4 h-4" />
                  Light a Torch
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" variant="outline" className="gap-2">
                View Directory
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
